import React, { useState } from 'react';
import { Card, Form, Input, Button, InputNumber, Typography, message, Radio, Row, Col, Switch, Space } from 'antd';
import { ArrowLeftOutlined, ThunderboltOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { videoScriptService } from '../../services/videoScriptService';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

type ScriptFormat = 'SCENE' | 'STORYBOARD' | 'NARRATION' | string;

const SCRIPT_FORMAT_OPTIONS: Array<{
  value: ScriptFormat;
  label: string;
  desc: string;
  example: string;
}> = [
  {
    value: 'SCENE',
    label: '集-场台本（真人短剧/影视）',
    desc: '按“第X集 → 场X-1/场X-2 → 内/外 + 日/夜 + 地点 → △动作 → 【SFX】/【BGM】 → 台词/OS”组织。',
    // 字段结构与后端 output_format.scene 模板的格式规则对齐（示例文本为前端演示用，非同源联动）
    example: `第01集 《替身》

场1-1  内  日  地点：老宅客厅
△林晚攥着病危通知单，指节发白。
【BGM】压抑弦乐（渐入）
林晚：钱……我一定想办法。
管家（递上合同）：签了它，一百万，今晚到账。

场1-2  内  夜  地点：豪宅书房
△林晚落笔签字，抬头撞上墙上遗像——脸和自己一模一样。
【SFX】钢笔落地声
林晚 OS：这不是替身合同……是替死合同。

场1-3  外  夜  地点：豪宅大门
△林晚转身想走，铁门缓缓合拢。
【SFX】铁门锁死（闷响）
管家（微笑）：先生，从现在起，您就是二少爷了。`,
  },
  {
    value: 'STORYBOARD',
    label: '分镜脚本（漫剧/动态漫）',
    desc: '按“镜头编号 → 时长/景别/运镜 → 画面 → 动效 → 音效/BGM → 台词”组织，便于动效/配音对轴。',
    // 字段结构与后端 output_format.storyboard 模板的格式规则对齐（示例文本为前端演示用，非同源联动）
    example: `镜头01  时长：3s  景别：特写  运镜：静止
画面：病危通知单上的红章，被指尖攥出褶皱
动效：纸面轻颤
音效/BGM：【SFX】心跳声（渐强）
台词：无

镜头02  时长：5s  景别：中景  运镜：慢推
画面：管家把合同推到林晚面前，金笔在灯下反光
动效：灯光微晃
音效/BGM：【BGM】压抑弦乐（轻）
台词：
- 管家：签了它，一百万今晚到账。
- 林晚（迟疑）：只是……当替身？

镜头03  时长：4s  景别：近景  运镜：急推
画面：墙上遗像的脸，与林晚一模一样
动效：画面骤然拉近后定格
音效/BGM：【SFX】惊悚音效（重击）
台词：
- 旁白：他这才明白，替身合同，其实是替死合同。`,
  },
  {
    value: 'NARRATION',
    label: '解说口播文案（竖屏解说视频）',
    desc: '纯口播文案带时间戳，第一人称叙事，可直接念/配音，不含“画面/字幕/音效”等技术标注。',
    // 字段结构与后端 output_format.narration 模板的格式规则对齐（示例文本为前端演示用，非同源联动）
    example: `【本集标题】我给富豪当替身，他却想让我替死
【时长】60秒
【解说文案】
00:00 - 00:03：我叫林晚，三天前，我签下了这辈子最后悔的一份合同。
00:03 - 00:07：给一个从没见过面的富豪二少爷当替身，一个月一百万。
00:07 - 00:11：我需要钱，我妈还躺在ICU里，等着这笔救命钱。
00:11 - 00:15：入住豪宅第一晚，管家收走了我的手机和身份证。
00:15 - 00:19：我安慰自己，有钱人规矩大点，正常。
00:19 - 00:24：直到我在书房看到那张遗像——照片上的人，和我长得一模一样。
00:24 - 00:28：更可怕的是，遗像下面压着一张体检单，名字写的是我。
00:28 - 00:33：我冲向大门，铁门在我面前缓缓合上。
00:33 - 00:38：管家站在楼梯上，笑着说：二少爷，您该吃药了。
00:38 - 00:43：那一刻我才明白，他们要的不是替身，是替死鬼。
00:43 - 00:48：跑，妈妈就没救了；留下，死的就是我。
00:48 - 00:53：我深吸一口气，转身走向管家：药，我吃。
00:53 - 01:00：但我的袖子里，藏着一把从餐桌上顺来的水果刀。
【结尾悬念】只是我没想到，真正的二少爷，一直躲在阁楼上看着我。
【下一集引子】第二天清晨，我在早餐里发现一张字条：别吃药，救我。`,
  },
];

const VideoScriptCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const result = await videoScriptService.create(values);
      message.success('创建成功！');

      const data = (result as any).data || result;
      if (data?.id) {
        navigate(`/video-scripts/${data.id}`);
      }
    } catch (error: any) {
      message.error(error.message || '创建失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/video-scripts')}
        style={{ marginBottom: '16px', paddingLeft: 0 }}
      >
        返回列表
      </Button>

      <Card bordered={false} style={{ borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Title level={2}>
            <VideoCameraOutlined /> 一键生成短视频剧本
          </Title>
          <Paragraph type="secondary">
            输入构思，AI 自动完成：系列设定 → 系列大纲 → 每集看点 → 导语 → 循环生成每一集（含审稿/连续性分析/动态更新）
          </Paragraph>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            targetSeconds: 60,
            episodeCount: 10,
            enableOutlineUpdate: true,
            minPassScore: 7,
            mode: 'HALF_NARRATION',
            scriptFormat: 'STORYBOARD',
          }}
        >
          <Form.Item name="title" label="剧本标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input size="large" placeholder="给你的短视频起个标题" />
          </Form.Item>

          <Form.Item
            name="idea"
            label="构思"
            rules={[{ required: true, message: '请输入构思' }]}
            help="描述越具体越好（题材、角色、冲突、反转、结局、想要的画面感）。"
          >
            <TextArea
              rows={6}
              placeholder={
                '写清题材、主要角色、核心冲突、反转、结局和想要的画面感。\n' +
                '例如：一个穷小子为了救母亲，去给富豪当替身，结果发现富豪想让他替死...'
              }
              showCount
              maxLength={2000}
            />
          </Form.Item>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item name="episodeCount" label="计划集数" help="可后续增加集数继续生成。">
                <InputNumber style={{ width: '100%' }} min={1} max={200} step={1} addonAfter="集" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="targetSeconds" label="每集目标时长">
                <InputNumber style={{ width: '100%' }} min={15} max={300} step={5} addonAfter="秒" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item name="sceneCount" label="每集镜头数量" extra="可不填，默认按 3 秒一个镜头自动估算。">
                <InputNumber style={{ width: '100%' }} min={5} max={200} step={1} addonAfter="个" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="minPassScore" label="审稿通过分" help="1-10，分数越高越严格。">
                <InputNumber style={{ width: '100%' }} min={1} max={10} step={1} addonAfter="分" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="enableOutlineUpdate" label="动态更新大纲" valuePropName="checked">
            <Switch checkedChildren="开启" unCheckedChildren="关闭" />
          </Form.Item>

          <Form.Item name="mode" label="输出模式">
            <Radio.Group>
              <Radio value="HALF_NARRATION">半解说（解说 + 人物对话）</Radio>
              <Radio value="PURE_NARRATION">纯解说（全程旁白）</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="scriptFormat"
            label="剧本格式"
            rules={[{ required: true, message: '请选择剧本格式' }]}
            help="决定“每集正文”的组织方式。"
          >
            <Radio.Group>
              <Radio value="SCENE">集-场台本（真人短剧/影视）</Radio>
              <Radio value="STORYBOARD">分镜脚本（漫剧/动态漫）</Radio>
              <Radio value="NARRATION">解说口播文案（竖屏解说视频）</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item shouldUpdate={(prev, cur) => prev.scriptFormat !== cur.scriptFormat} noStyle>
            {() => {
              const fmt = (form.getFieldValue('scriptFormat') || 'STORYBOARD') as ScriptFormat;
              const opt = SCRIPT_FORMAT_OPTIONS.find((o) => o.value === fmt) || SCRIPT_FORMAT_OPTIONS[1];

              const copyExample = async () => {
                try {
                  await navigator.clipboard.writeText(opt.example);
                  message.success('已复制格式示例');
                } catch {
                  message.warning('复制失败，请手动选择文本复制');
                }
              };

              return (
                <Form.Item label={`格式示例（${opt.label}）`} help={opt.desc + '（仅示意结构，不必照抄剧情内容）'}>
                  <TextArea rows={10} value={opt.example} readOnly style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }} />
                  <Space style={{ marginTop: 8 }}>
                    <Button size="small" onClick={copyExample}>
                      复制示例
                    </Button>
                  </Space>
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item style={{ marginTop: '24px' }}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={loading}
              icon={<ThunderboltOutlined />}
              style={{ height: '48px', fontSize: '18px', borderRadius: '8px' }}
            >
              创建系列
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default VideoScriptCreatePage;
